/**
 * Icon resolution.
 *
 * By default Web Awesome's icon library fetches SVGs from the Font Awesome CDN
 * at runtime. KinCode does not accept that:
 *
 *   - Privacy. A CDN request discloses the visitor's IP and the page they are on
 *     to a third party. §14 forbids behavioural tracking on the retrieval
 *     journey, and a request made by a stranger recovering a contact is exactly
 *     the traffic that must not leak.
 *   - Availability. Retrieval is the one journey that must work. A third-party
 *     outage, a blocked domain or a slow network must not stop an icon — or the
 *     page — from rendering.
 *   - Content Security Policy. Phase 5 hardens CSP. Not having a CDN origin to
 *     allowlist is simpler than allowlisting one.
 *
 * The icons are therefore self-hosted in `public/icons`, copied from the Font
 * Awesome Pro kit package under the user's Pro entitlement. The directory
 * mirrors the Font Awesome SVG download structure that `setIconPath` expects:
 * `{path}/{style}/{name}.svg`.
 *
 * This module exists separately from `main.js` because ES module imports are
 * hoisted and evaluated before any statement in the importing module. Calling
 * `setIconPath()` inside `main.js` would therefore run *after* the component
 * imports had already been evaluated, which is too late. Importing this module
 * first guarantees the path is set before any component loads.
 */

import { setIconPath } from "@web.awesome.me/webawesome-pro/dist/utilities/base-path.js";

/**
 * Where self-hosted icon SVGs are served from, relative to the site root.
 *
 * Vite serves `public/` at `/`, so `public/icons/solid/key.svg` is `/icons/solid/key.svg`.
 */
const ICON_PATH = "/icons";

setIconPath(ICON_PATH);

/**
 * The single icon family used across the application.
 *
 * §4 requires a consistent icon style. Components reference this rather than
 * repeating the family name, so changing it is a one-line edit.
 *
 * @type {string}
 */
export const ICON_FAMILY = "solid";
